import { useLocation } from "wouter";
import { PenLine } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useMyContractDocuments } from "@/hooks/use-contract-documents";
import { Button } from "@/components/ui/button";

/**
 * "You have N documents to sign" — amendments and new agreements from Sidekick
 * waiting on this PA. Same shape as the profile-completion banner, in blue so
 * the two don't read as one warning when both show. Silent while loading or on
 * error: the /legal-documents page owns the error state.
 */
export default function DocumentsToSignBanner() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { data: docs } = useMyContractDocuments(!!user?.uid);

  const count = (docs ?? []).filter((doc) => doc.can_sign).length;
  if (count === 0) return null;

  return (
    <div
      role="status"
      className="mb-4 rounded-xl border border-blue-200 bg-blue-50/80 dark:border-blue-900/40 dark:bg-blue-950/30"
    >
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5 sm:py-4">
        <div className="flex items-start gap-3 min-w-0">
          <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            <PenLine className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
              You have {count} {count === 1 ? "document" : "documents"} to sign
            </p>
            <p className="mt-0.5 text-xs sm:text-sm text-blue-800/90 dark:text-blue-300/80">
              Sidekick has sent you {count === 1 ? "a document" : "documents"} that need your signature.
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 sm:pl-4">
          <Button
            size="sm"
            onClick={() => setLocation("/legal-documents")}
            className="bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600 dark:text-blue-950"
          >
            Review
          </Button>
        </div>
      </div>
    </div>
  );
}
